/**
 * Shared MIME-type-to-icon mapping (ELARA-739 follow-up).
 *
 * Both the web and native attachment previews (`MessageAttachments`, `ChatComposer`/`ChatInputView`)
 * need to pick an icon for a non-image file attachment. This is the single source of truth for that
 * mapping so the two platforms never drift — web additionally maps each type to a Tailwind color
 * class, which is presentation-only and stays local to `client/src/lib/file-utils.ts`.
 */
import type { IconName } from '../ui-contracts/icon-name.js';

/**
 * Maps a file's MIME type — or, when absent, its name's extension — to a Material Symbols icon name.
 * Falls back to `unknown_document` when neither is recognized.
 */
export function getFileTypeIconName(mimeType?: string, fileName?: string): IconName {
  if (mimeType) {
    if (mimeType.startsWith('image/')) return 'image';
    if (mimeType === 'application/pdf') return 'picture_as_pdf';
    if (
      mimeType === 'application/msword' ||
      mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ) {
      return 'description';
    }
    if (
      mimeType === 'application/vnd.ms-excel' ||
      mimeType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ) {
      return 'table_chart';
    }
    if (
      mimeType === 'application/vnd.ms-powerpoint' ||
      mimeType === 'application/vnd.openxmlformats-officedocument.presentationml.presentation'
    ) {
      return 'image';
    }
    if (mimeType.startsWith('text/')) return 'description';
    if (
      mimeType === 'application/zip' ||
      mimeType === 'application/x-zip-compressed' ||
      mimeType === 'application/x-rar-compressed' ||
      mimeType === 'application/x-7z-compressed'
    ) {
      return 'folder_zip';
    }
    if (mimeType.startsWith('audio/')) return 'audio_file';
    if (mimeType.startsWith('video/')) return 'video_file';
    if (
      mimeType === 'application/javascript' ||
      mimeType === 'application/typescript' ||
      mimeType === 'application/json' ||
      mimeType === 'application/xml'
    ) {
      return 'code';
    }
  }

  const ext = fileName?.split('.').pop()?.toLowerCase();
  switch (ext) {
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
    case 'webp':
    case 'svg':
    case 'bmp':
      return 'image';
    case 'pdf':
      return 'picture_as_pdf';
    case 'doc':
    case 'docx':
      return 'description';
    case 'xls':
    case 'xlsx':
      return 'table_chart';
    case 'ppt':
    case 'pptx':
      return 'image';
    case 'txt':
    case 'md':
    case 'rtf':
      return 'description';
    case 'js':
    case 'ts':
    case 'jsx':
    case 'tsx':
    case 'json':
    case 'xml':
    case 'html':
    case 'css':
    case 'py':
    case 'java':
    case 'cpp':
    case 'c':
    case 'h':
      return 'code';
    case 'zip':
    case 'rar':
    case '7z':
    case 'tar':
    case 'gz':
      return 'folder_zip';
    case 'mp3':
    case 'wav':
    case 'flac':
    case 'aac':
    case 'ogg':
      return 'audio_file';
    case 'mp4':
    case 'avi':
    case 'mov':
    case 'wmv':
    case 'flv':
    case 'webm':
      return 'video_file';
    default:
      return 'unknown_document';
  }
}
